/**
 * FundingBreakdownPanel — Stacked bar + table showing funding sources
 * grouped by type for the Budget subtab within Performance.
 *
 * Mirrors the CostBreakdownPanel visual pattern: metric cards → stacked
 * bar → compact table. Also shows a budget coverage progress bar.
 *
 * Categorical segment colors use the same non-reserved palette as
 * CostBreakdownPanel (no blue/green/yellow/red for categories).
 * Status badges DO use reserved colors since they represent actual status.
 *
 * Standard tier — no Gantt/EVM dependency.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchFundingSources,
  fetchFundingSummary,
  apiResponseToFundingSource,
  FUNDING_SOURCE_TYPES,
} from "../../api/fundingSources";
import type {
  FundingSource,
  FundingSourceType,
  FundingStatus,
  FundingSummaryResponse,
} from "../../api/fundingSources";
import { formatCurrencyCompact } from "../../Data/Budget/currencyLookup";
import styles from "./Navigation.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface FundingBreakdownPanelProps {
  projectGuid: string;
  budgetCurrency: string;
  /** Total estimated cost (from CostBreakdownPanel) for coverage calc */
  totalEstimatedCost?: number;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

interface TypeRow {
  type: FundingSourceType;
  pledged: number;
  received: number;
  count: number;
}

/**
 * Same non-reserved palette as CostBreakdownPanel.
 * Avoids blue/green/yellow/red on Performance surfaces.
 */
const FUNDING_TYPE_COLORS = [
  "hsl(270, 50%, 58%)",  // purple
  "hsl(175, 50%, 42%)",  // teal
  "hsl(20,  65%, 55%)",  // coral
  "hsl(325, 45%, 55%)",  // pink
  "hsl(195, 40%, 50%)",  // steel
  "hsl(35,  55%, 50%)",  // tan
  "hsl(290, 35%, 50%)",  // plum
  "hsl(160, 35%, 45%)",  // sage
  "hsl(15,  50%, 48%)",  // sienna
];

const FUNDING_TYPE_TEXT_COLORS = [
  "hsl(270, 80%, 92%)",
  "hsl(175, 60%, 90%)",
  "hsl(20,  80%, 92%)",
  "hsl(325, 60%, 92%)",
  "hsl(195, 50%, 90%)",
  "hsl(35,  60%, 92%)",
  "hsl(290, 50%, 90%)",
  "hsl(160, 50%, 90%)",
  "hsl(15,  60%, 90%)",
];

/** Status badges use reserved colors because they ARE status indicators */
function statusBadgeStyle(status: FundingStatus): { bg: string; text: string } {
  switch (status) {
    case "received":  return { bg: "var(--info-text)",      text: "#fff" };       // Blue — Done
    case "confirmed": return { bg: "var(--color-success)",  text: "#fff" };       // Green — On track
    case "pending":   return { bg: "var(--color-warning)",  text: "#000" };       // Yellow — Needs attention
    case "declined":  return { bg: "var(--color-error)",    text: "#fff" };       // Red — Critical
    case "withdrawn": return { bg: "var(--text-muted)",     text: "#fff" };       // Grey — N/A
    default:          return { bg: "var(--text-muted)",     text: "#fff" };
  }
}

const MIN_LABEL_PCT = 18;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const FundingBreakdownPanel: React.FC<FundingBreakdownPanelProps> = ({
  projectGuid,
  budgetCurrency,
  totalEstimatedCost = 0,
  debugMode,
}) => {
  const { t: tKpi } = useAppTranslation("kpi");

  /* ── Fetch funding sources ── */
  const [sources, setSources] = useState<FundingSource[]>([]);
  const [summary, setSummary] = useState<FundingSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadFunding = useCallback(async () => {
    if (!projectGuid) return;
    setLoading(true);
    try {
      const [srcResp, summResp] = await Promise.all([
        fetchFundingSources(projectGuid),
        fetchFundingSummary(projectGuid),
      ]);
      setSources(srcResp.map(apiResponseToFundingSource));
      setSummary(summResp);
    } catch (err: any) {
      console.warn("[FundingBreakdown] Failed to load:", err.message);
    } finally {
      setLoading(false);
    }
  }, [projectGuid]);

  useEffect(() => { loadFunding(); }, [loadFunding]);

  useEffect(() => {
    const handler = () => loadFunding();
    window.addEventListener("l4e_funding_changed", handler);
    return () => window.removeEventListener("l4e_funding_changed", handler);
  }, [loadFunding]);

  /* ── Rollups by source type ── */
  const { byType, totalPledged, totalReceived } = useMemo(() => {
    const typeMap: Record<string, TypeRow> = {};
    for (const st of FUNDING_SOURCE_TYPES) {
      typeMap[st] = { type: st, pledged: 0, received: 0, count: 0 };
    }

    let totPledged = 0;
    let totReceived = 0;

    for (const src of sources) {
      const t = typeMap[src.sourceType];
      if (t) {
        t.pledged += src.amountPledged;
        t.received += src.amountReceived;
        t.count++;
      }
      totPledged += src.amountPledged;
      totReceived += src.amountReceived;
    }

    const byT = Object.values(typeMap)
      .filter((r) => r.count > 0)
      .sort((a, b) => b.pledged - a.pledged);

    return { byType: byT, totalPledged: totPledged, totalReceived: totReceived };
  }, [sources]);

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className={styles.breakdownPanel}>
        <div className={styles.breakdownTitle}>
          {tKpi("budget.funding.title")}
        </div>
        <div className={styles.breakdownEmpty} style={{ fontStyle: "normal" }}>
          …
        </div>
      </div>
    );
  }

  /* ── Empty state ── */
  if (sources.length === 0) {
    return (
      <div className={styles.breakdownPanel}>
        <div className={styles.breakdownTitle}>
          {tKpi("budget.funding.title")}
        </div>
        <div className={styles.breakdownEmpty}>
          {tKpi("budget.funding.empty")}
        </div>
      </div>
    );
  }

  /* ── Render ── */
  const coveragePct = totalEstimatedCost > 0
    ? Math.min(999, Math.round((totalPledged / totalEstimatedCost) * 100))
    : 0;

  return (
    <div className={styles.breakdownPanel}>
      {/* Section header */}
      <div className={styles.breakdownTitle}>
        {tKpi("budget.funding.title")}
      </div>

      {/* Metric cards */}
      <div className={styles.breakdownMetricRow}>
        <div className={styles.breakdownMetricCard}>
          <span className={styles.breakdownMetricLabel}>
            {tKpi("budget.funding.pledged")}
          </span>
          <span className={styles.breakdownMetricValue}>
            {formatCurrencyCompact(totalPledged, budgetCurrency)}
          </span>
        </div>
        <div className={styles.breakdownMetricCard}>
          <span className={styles.breakdownMetricLabel}>
            {tKpi("budget.funding.received")}
          </span>
          <span className={styles.breakdownMetricValue}>
            {formatCurrencyCompact(totalReceived, budgetCurrency)}
          </span>
        </div>
      </div>

      {/* Stacked bar (by funding type) */}
      <div className={styles.stackedBar}>
        {byType.map((row, idx) => {
          const pct = totalPledged > 0
            ? (row.pledged / totalPledged) * 100
            : 0;
          if (pct < 0.5) return null;
          return (
            <div
              key={row.type}
              className={styles.stackedBarSegment}
              style={{
                width: `${pct}%`,
                background: FUNDING_TYPE_COLORS[idx % FUNDING_TYPE_COLORS.length],
              }}
            >
              {pct >= MIN_LABEL_PCT && (
                <span
                  className={styles.stackedBarSegmentLabel}
                  style={{ color: FUNDING_TYPE_TEXT_COLORS[idx % FUNDING_TYPE_TEXT_COLORS.length] }}
                >
                  {tKpi(`budget.funding.types.${row.type}`)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Table — one row per funding source (not type) for detail */}
      <table className={styles.breakdownTable}>
        <thead>
          <tr>
            <th className={styles.breakdownTh} style={{ textAlign: "start" }}>
              {tKpi("budget.funding.sourceCol")}
            </th>
            <th className={styles.breakdownTh}>
              {tKpi("budget.funding.pledged")}
            </th>
            <th className={styles.breakdownTh}>
              {tKpi("budget.funding.recvAbbr")}
            </th>
            <th className={styles.breakdownTh}>
              {tKpi("budget.funding.statusCol")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sources.map((src, idx) => {
            const badge = statusBadgeStyle(src.status);
            // Find which type-color index this source gets
            const typeIdx = byType.findIndex((t) => t.type === src.sourceType);
            const dotColor = FUNDING_TYPE_COLORS[(typeIdx >= 0 ? typeIdx : idx) % FUNDING_TYPE_COLORS.length];
            return (
              <tr key={src.id} className={styles.breakdownTr}>
                <td className={styles.breakdownTdName}>
                  <span
                    className={styles.breakdownColorDot}
                    style={{ background: dotColor }}
                  />
                  {src.sourceName || tKpi(`budget.funding.types.${src.sourceType}`)}
                </td>
                <td className={styles.breakdownTdNum}>
                  {formatCurrencyCompact(src.amountPledged, budgetCurrency)}
                </td>
                <td className={styles.breakdownTdNum}>
                  {formatCurrencyCompact(src.amountReceived, budgetCurrency)}
                </td>
                <td className={styles.breakdownTdStatus}>
                  <span
                    className={styles.breakdownStatusBadge}
                    style={{ background: badge.bg, color: badge.text }}
                  >
                    {tKpi(`budget.funding.statuses.${src.status}`)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Budget coverage bar */}
      {totalEstimatedCost > 0 && (
        <div className={styles.coverageRow}>
          <div className={styles.coverageHeader}>
            <span className={styles.coverageLabel}>
              {tKpi("budget.funding.coverage")}
            </span>
            <span className={styles.coverageValue}>
              {coveragePct}%
            </span>
          </div>
          <div className={styles.coverageTrack}>
            <div
              className={styles.coverageFill}
              style={{ width: `${Math.min(100, coveragePct)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FundingBreakdownPanel;
